import {
  PERFORMANCE_ACTS,
  EVENT_WORLDS,
  EVENT_CONCEPTS,
  CLIENTS,
  PORTFOLIO,
  BRAND,
} from "./content";

// -------- Deep act pages -------
const ACT_GALLERIES = {
  "golden-performer": [
    "/generated/golden-performer-1.jpg",
    "/generated/golden-performer-3.jpg",
    "/generated/golden-performer-2.jpg",
  ],
  "fire-performance": [
    "/generated/fire-performance-1.jpg",
    "/generated/fire-performance-2.webp",
  ],
};

export const ACT_DETAILS = PERFORMANCE_ACTS.map((a) => ({
  ...a,
  slug: a.id,
  headline: a.name,
  intro: a.desc,
  longIntro:
    a.desc +
    "Each performance is bespoke — costumes, choreography and set-up tailored to your evening's palette, venue and audience.",
  duration: "6–20 minutes",
  cast: "Solo — to full ensemble",
  rider: [
    "Stage or performance zone (min 5×5 m)",
    "Separate warm-up & wardrobe room",
    "Audio (playback or live DJ / band sync)",
    "Rigging (for aerial) & fire clearance (for fire)",
  ],
  gallery: ACT_GALLERIES[a.id] || [
    a.image,
    "/generated/white-party-2.jpg",
    "/generated/tribal-show.jpg",
    "/generated/white-party-1.jpg",
  ],
}));

// -------- Concept pages (event worlds + newer concepts) -------
export const CONCEPT_DETAILS = [
  ...EVENT_WORLDS.map((w) => ({
    slug: w.id,
    name: w.name,
    tags: w.name.split(" ").slice(0, 3),
    image: w.image,
    video: w.video,
    intro: w.desc,
    _customGallery: w.gallery,
  })),
  ...EVENT_CONCEPTS.map((c) => ({
    slug: c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    name: c.name,
    tags: c.tags,
    image: c.image,
    video: c.video,
    intro: `A signature ${c.name} experience — designed end-to-end from styling to soundtrack.`,
    _customGallery: c.gallery,
  })),
].map((c) => ({
  ...c,
  longIntro:
    c.intro +
    "We take care of every level of the world — decor, live acts, taste pairings, lighting and after-party — so you can experience the evening stress-free.",
  gallery: c._customGallery && c._customGallery.length > 0
    ? c._customGallery
    : [
        c.image,
        "/generated/sicilian-dolce-2.jpg",
        "/generated/white-party-2.jpg",
        "/generated/golden-performer-1.jpg",
      ],
}));

// -------- Client event-type pages -------
export const EVENT_PAGES = CLIENTS.map((c) => {
  const slug = c.slug;
  return {
    slug,
    name: c.name,
    icon: c.icon,
    heroImage: {
      "Luxury Weddings":
        "/generated/arabian-nights-5.jpg",
      "Private Parties":
        "/generated/arabian-nights-4.jpg",
      "Corporate Events":
        "/generated/white-party-3.jpg",
      "Hotels & Resorts":
        "/generated/arabian-nights-3.jpg",
      "Brand Activations":
        "/generated/wedding-cultural.jpg",
      "Destination Events":
        "/generated/arabian-nights-2.jpg",
    }[c.name],
    kicker: "Who we work with",
    intro:
      {
        "Luxury Weddings":
          "From private villas on the Amalfi Coast to grand ballrooms in Athens — we produce wedding entertainment where every detail whispers 'once in a lifetime'.",
        "Private Parties":
          "Birthdays, anniversaries, private galas — bespoke evenings that transform a guest list into a story.",
        "Corporate Events":
          "Galas, kick-offs, awards, product launches — cinematic entertainment that carries your brand message with elegance.",
        "Hotels & Resorts":
          "Signature acts, grand openings and season programming for the world's most sophisticated properties.",
        "Brand Activations":
          "Editorial-grade experiential activations that make your brand impossible to ignore — and impossible to forget.",
        "Destination Events":
          "Multi-day productions in Europe, the Mediterranean and the Middle East — we handle logistics, permits and all performers on-site.",
      }[c.name] || "Bespoke entertainment for the most demanding hosts.",
    gallery: [
      "/generated/sicilian-dolce-1.jpg",
      "/generated/fire-performance-2.jpg",
      "/generated/fire-performance-1.jpg",
      "/generated/arabian-nights-3.jpg",
    ],
  };
});

// -------- Journal / blog stubs -------
export const JOURNAL_POSTS = [
  {
    slug: "the-art-of-a-golden-entrance",
    title: "The Art of a Golden Entrance",
    excerpt:
      "Why the first minute of a night determines the memory of the entire evening — and how we choreograph it.",
    date: "November 2025",
    image:
      "/generated/white-party-3.jpg",
  },
  {
    slug: "flame-and-feather-tribal-nights",
    title: "Flame & Feather: Designing a Tribal Night",
    excerpt:
      "A behind-the-scenes look at our ethnic-ritual concept, from headdress craftsmanship to sunset choreography.",
    date: "October 2025",
    image: "/generated/tribal-show.jpg",
  },
  {
    slug: "andalusian-flamenco-notes",
    title: "Notes from an Andalusian Evening",
    excerpt:
      "How we pair jamón, sangria and a live flamenco act into a single, storied experience.",
    date: "September 2025",
    image: "/generated/andalusian-flamenco.jpg",
  },
];

// -------- FAQ -------
export const FAQS = [
  {
    q: "How far in advance should we book?",
    a: "For weddings and major galas we recommend 6–9 months. Corporate activations and hotel openings we can turn around in as little as 6 weeks, depending on scale.",
  },
  {
    q: "Do you travel internationally?",
    a: "Yes — we have produced events in 12 countries across Europe, the Mediterranean and the Middle East. We handle performer visas, technical logistics and local permits.",
  },
  {
    q: "Can we mix multiple concepts in one night?",
    a: "Absolutely. Many of our clients open with a Golden Performer welcome, transition into a Sicilian Sunset dinner, and close with a Disco 80's after-party. Everything is bespoke.",
  },
  {
    q: "What's included in a full production?",
    a: "Concept development, casting, choreography, costumes, staging, lighting, sound, rigging (aerial), fire clearance, run-of-show, and on-site direction on the night of.",
  },
  {
    q: "Do you provide DJs and live music?",
    a: "Yes — we have a full roster of live musicians, string quartets, jazz trios, saxophonists, live vocalists and internationally-touring DJs.",
  },
  {
    q: "How does pricing work?",
    a: "Every event is quoted bespoke based on scale, venue, performers and technical requirements. After a discovery call we send a curated proposal within 48 hours.",
  },
];

// -------- Press -------
export const PRESS = [
  {
    outlet: "Vogue Italia",
    quote: "A masterclass in cinematic hospitality.",
    date: "2025",
  },
  {
    outlet: "Condé Nast Traveler",
    quote:
      "Lumière transforms private villas into worlds you don't want to leave.",
    date: "2025",
  },
  {
    outlet: "Hospitality Design",
    quote:
      "One of the most ambitious event producers to emerge from the Mediterranean.",
    date: "2024",
  },
  {
    outlet: "Bridal Musings",
    quote: "The gold standard for luxury wedding entertainment.",
    date: "2024",
  },
];

export { BRAND, PORTFOLIO };
